import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  scaleGatewayApi,
  scaleLinkApi,
  type ScaleGatewayGap,
  type ScaleGatewayReportListItem,
  type ScaleLinkStatus,
} from "../../api/scaleGatewayApi";
import { useAuth } from "../../auth/AuthContext";
import { canAccessEasyRetailReports } from "../../auth/roles";

function toLocal(value?: string | null) {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString();
}

function statusBadge(status?: string) {
  const s = String(status || "").toUpperCase();
  if (s === "OK") return "text-bg-success";
  if (s === "PARTIAL") return "text-bg-warning";
  if (s === "ERROR") return "text-bg-danger";
  return "text-bg-secondary";
}

function severityBadge(severity: string) {
  const s = String(severity || "").toUpperCase();
  if (s === "HIGH") return "text-bg-danger";
  if (s === "MEDIUM") return "text-bg-warning";
  if (s === "LOW") return "text-bg-secondary";
  return "text-bg-info";
}

function GapTable({ gaps }: { gaps: ScaleGatewayGap[] }) {
  if (!gaps.length) return <div className="text-muted">Nessun problema rilevato.</div>;
  return (
    <div className="table-responsive">
      <table className="table table-sm align-middle">
        <thead>
          <tr>
            <th>Severità</th>
            <th>Area</th>
            <th>Rilevazione</th>
            <th>Azione</th>
          </tr>
        </thead>
        <tbody>
          {gaps.map((g) => (
            <tr key={g.id}>
              <td><span className={`badge ${severityBadge(g.severity)}`}>{g.severity}</span></td>
              <td>{g.area}</td>
              <td>
                <div className="fw-semibold">{g.title}</div>
                <div className="small text-muted">{g.detail}</div>
              </td>
              <td className="small">{g.action || "-"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DiagnosticsChecklist({
  rows,
}: {
  rows: Array<{ id: string; label: string; ok: boolean; detail: string; recordCount?: number; invalidCount?: number }>;
}) {
  if (!rows.length) return <div className="text-muted">Nessuna diagnostica nel report.</div>;
  return (
    <ul className="list-group list-group-flush">
      {rows.map((r) => (
        <li key={r.id} className="list-group-item d-flex justify-content-between gap-2 px-0">
          <span>
            {r.ok ? "✓" : "✗"} {r.label}
          </span>
          <span className="text-muted small text-end">
            {r.recordCount ?? 0} rec.
            {r.invalidCount ? ` · ${r.invalidCount} invalidi` : ""} — {r.detail}
          </span>
        </li>
      ))}
    </ul>
  );
}

const ZANARDELLI_SCALES = [
  { id: "1", name: "Bilancia 1", host: "192.168.1.111" },
  { id: "2", name: "Bilancia 2", host: "192.168.1.112" },
];

function Lamp({ on, color, label }: { on: boolean; color: "green" | "red"; label: string }) {
  const background = on ? (color === "green" ? "#198754" : "#dc3545") : "#dee2e6";
  return (
    <div className="d-flex align-items-center gap-2">
      <span
        aria-hidden
        style={{
          width: 22,
          height: 22,
          borderRadius: "50%",
          background,
          boxShadow: on ? `0 0 10px ${background}` : "none",
          border: "2px solid rgba(0,0,0,0.15)",
        }}
      />
      <span className="small fw-semibold">{label}</span>
    </div>
  );
}

function ScaleLinkPanel() {
  const { token } = useAuth();
  const [link, setLink] = useState<ScaleLinkStatus | null>(null);
  const [loading, setLoading] = useState<"status" | "articles" | "">("");
  const [error, setError] = useState("");

  async function run(kind: "status" | "articles") {
    if (!token) return;
    setLoading(kind);
    setError("");
    try {
      const res =
        kind === "status"
          ? await scaleLinkApi.status(token, ZANARDELLI_SCALES)
          : await scaleLinkApi.articles(token, ZANARDELLI_SCALES);
      setLink(res);
    } catch (e: any) {
      setError(e?.message || "Collegamento bilance non riuscito");
    } finally {
      setLoading("");
    }
  }

  return (
    <div className="card shadow-sm mb-3">
      <div className="card-body">
        <h2 className="h6 mb-1">Waage / Alenia — Via Zanardelli</h2>
        <p className="text-muted small mb-3">
          Bilancia 1 <code>192.168.1.111</code> e bilancia 2 <code>192.168.1.112</code>. Il socket lo apre NATPOS sul PC cassa di Via Zanardelli. Questa pagina elenca gli IP; la spia dal vivo è nella sezione comunicazione di NATPOS.
        </p>
        {error ? <div className="alert alert-danger py-2">{error}</div> : null}
        <div className="row g-3">
          {ZANARDELLI_SCALES.map((scale) => {
            const row = link?.scales.find((item) => item.id === scale.id);
            const connected = row?.connected === true;
            return (
              <div key={scale.id} className="col-md-6">
                <div className="border rounded p-3 h-100">
                  <div className="fw-semibold">{scale.name}</div>
                  <div className="small text-muted mb-2">IP {scale.host}</div>
                  <div className="d-flex gap-3">
                    <Lamp on={connected} color="green" label="collegata" />
                    <Lamp on={row != null && !connected} color="red" label="non collegata" />
                  </div>
                  <div className="small mt-2">{row?.message || "Lettura non ancora eseguita."}</div>
                </div>
              </div>
            );
          })}
        </div>
        <div className="border rounded p-3 mt-3">
          <div className="fw-semibold">Driver</div>
          <div className="small mt-1">
            Nel gestionale il driver è già presente: parser Alenia e WAAGE, file articoli <code>C:\ALENIA_Z\Asciiplu.asr</code>.
          </div>
          <div className="small text-muted">
            Sul PC di Via Zanardelli deve essere installato il software Alenia / WAAGE nella cartella <code>C:\ALENIA_Z</code>. Quel programma carica gli articoli sulle due bilance. Se Corevian non è quel PC, la spia può essere rossa anche con le bilance accese.
          </div>
        </div>
        <div className="d-flex flex-wrap gap-2 mt-3">
          <button type="button" className="btn btn-sm btn-outline-primary" disabled={loading !== ""} onClick={() => void run("status")}>
            {loading === "status" ? "Lettura..." : "Prova dal server, solo se è in negozio"}
          </button>
          <button type="button" className="btn btn-sm btn-primary" disabled={loading !== ""} onClick={() => void run("articles")}>
            {loading === "articles" ? "Invio..." : "Invia articoli alle bilance"}
          </button>
        </div>
        {link?.articles ? (
          <div className={`alert py-2 small mt-3 mb-0 ${link.articles.written ? "alert-success" : "alert-warning"}`}>
            {link.articles.count} articoli pesabili pronti. {link.articles.message}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function ReportList({ onSelect }: { onSelect: (id: string) => void }) {
  const { token } = useAuth();
  const [items, setItems] = useState<ScaleGatewayReportListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError("");
    try {
      const res = await scaleGatewayApi.list(token, 50);
      setItems(res.items || []);
    } catch (e: any) {
      setError(e?.message || "Errore caricamento report bilance");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="card shadow-sm">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h2 className="h6 mb-0">Report bilance (Scale Gateway)</h2>
          <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => void load()} disabled={loading}>
            Aggiorna
          </button>
        </div>
        {error ? <div className="alert alert-danger py-2">{error}</div> : null}
        <div className="table-responsive">
          <table className="table table-hover table-sm align-middle mb-0">
            <thead>
              <tr>
                <th>Data</th>
                <th>POS</th>
                <th>Vendor</th>
                <th>Cartella dati</th>
                <th>Stato</th>
                <th>Problemi</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-muted">
                    {loading ? "Caricamento..." : "Nessun report. Invia diagnostica da NATPOS sulla cassa."}
                  </td>
                </tr>
              ) : null}
              {items.map((r) => (
                <tr key={r.id}>
                  <td className="small">{toLocal(r.analyzedAt)}</td>
                  <td>
                    <div>{r.posCode || "-"}</div>
                    <div className="small text-muted">{r.location || r.hostname || ""}</div>
                  </td>
                  <td>{r.vendor || "-"}</td>
                  <td className="small"><code>{r.dataDir || "-"}</code></td>
                  <td><span className={`badge ${statusBadge(r.status)}`}>{r.status}</span></td>
                  <td className="small">{r.summary?.invalidTotal ?? 0}</td>
                  <td>
                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => onSelect(r.id)}>
                      Dettaglio
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function ReportDetail({ reportId }: { reportId: string }) {
  const { token } = useAuth();
  const [data, setData] = useState<Awaited<ReturnType<typeof scaleGatewayApi.getById>> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token || !reportId) return;
    setLoading(true);
    setError("");
    scaleGatewayApi
      .getById(token, reportId)
      .then(setData)
      .catch((e: any) => setError(e?.message || "Errore"))
      .finally(() => setLoading(false));
  }, [token, reportId]);

  const report = data?.report;
  const gaps = useMemo(() => (Array.isArray(report?.gaps) ? report!.gaps : []), [report]);
  const diagnostics = useMemo(
    () => (Array.isArray(report?.diagnostics) ? report!.diagnostics : []),
    [report],
  );
  const findings = (report?.findings || {}) as Record<string, unknown>;
  const counts = (findings.counts || {}) as Record<string, number>;
  const capabilities = (report?.capabilities || {}) as Record<string, boolean>;
  const fileMap = (findings.fileMap || {}) as Record<string, string | null>;

  if (loading) return <div className="text-muted">Caricamento report...</div>;
  if (error) return <div className="alert alert-danger py-2">{error}</div>;
  if (!report) return null;

  return (
    <div className="d-grid gap-3">
      <div className="card shadow-sm">
        <div className="card-body">
          <div className="d-flex flex-wrap justify-content-between gap-2 mb-2">
            <div>
              <h2 className="h5 mb-1">Bilancia — {report.vendor || "N/D"}</h2>
              <div className="text-muted small">
                {report.posCode || "-"} · {toLocal(report.analyzedAt)} · {report.hostname || "-"}
              </div>
            </div>
            <span className={`badge ${statusBadge(report.status)} align-self-start`}>{report.status}</span>
          </div>
          <p className="mb-2">{(report.summary as { headline?: string })?.headline || "-"}</p>
          <div className="small text-muted mb-0">
            Cartella dati: <code>{report.dataDir || "-"}</code>
          </div>
        </div>
      </div>

      <div className="card shadow-sm">
        <div className="card-body">
          <h3 className="h6">Diagnostica database</h3>
          <DiagnosticsChecklist rows={diagnostics} />
          <div className="small text-muted mt-2">
            PLU {counts.plu ?? 0} · Operatori {counts.operators ?? 0} · Reparti {counts.departments ?? 0} ·
            Etichette {counts.labels ?? 0} · Barcode {counts.barcode ?? 0} · Totali {counts.totals ?? 0}
          </div>
        </div>
      </div>

      <div className="card shadow-sm">
        <div className="card-body">
          <h3 className="h6">File rilevati</h3>
          <ul className="small mb-0">
            {Object.entries(fileMap).map(([k, v]) => (
              <li key={k}>
                <strong>{k}</strong>: {v ? <code>{v}</code> : <span className="text-muted">non trovato</span>}
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="card shadow-sm">
        <div className="card-body">
          <h3 className="h6">Capacità NATPOS Scale Gateway</h3>
          <div className="d-flex flex-wrap gap-2">
            {Object.entries(capabilities).map(([k, v]) => (
              <span key={k} className={`badge ${v ? "text-bg-success" : "text-bg-secondary"}`}>
                {v ? "✓" : "○"} {k}
              </span>
            ))}
          </div>
          <p className="small text-muted mt-2 mb-0">
            Backup, lettura PLU e ricostruzione totali si eseguono sulla cassa (NATPOS Electron). La riparazione remota via rete è in sviluppo.
          </p>
        </div>
      </div>

      <div className="card shadow-sm">
        <div className="card-body">
          <h3 className="h6">Gap e azioni</h3>
          <GapTable gaps={gaps} />
        </div>
      </div>
    </div>
  );
}

export default function ScaleGatewayPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const canView = canAccessEasyRetailReports(user?.role);
  const [selectedId, setSelectedId] = useState<string | null>(id || null);

  useEffect(() => {
    if (id) setSelectedId(id);
  }, [id]);

  if (!canView) {
    return (
      <div className="container-fluid py-3">
        <div className="alert alert-warning">Accesso negato. Profilo Operatore/Manager/Admin richiesto.</div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-3">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <div>
          <h1 className="h4 mb-1">Bilance Waage / Alenia</h1>
          <p className="text-muted small mb-0">
            Via Zanardelli: collegamento IP e invio articoli. Sotto, i report di diagnostica inviati da NATPOS.
          </p>
        </div>
        <div className="d-flex gap-2">
          {selectedId ? (
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setSelectedId(null)}>
              Lista report
            </button>
          ) : null}
          <Link to="/reports/scales" className="btn btn-sm btn-outline-secondary">
            Bilance
          </Link>
          <Link to="/reports" className="btn btn-sm btn-outline-secondary">
            Tutti i report
          </Link>
        </div>
      </div>

      <ScaleLinkPanel />

      {!selectedId ? (
        <>
          <div className="alert alert-info py-2 small mb-3">
            Su NATPOS (cassa): sezione <strong>Bilance</strong> → <strong>Diagnostica integrità</strong> →{" "}
            <strong>Invia report bilance a Corevian</strong>.
          </div>
          <ReportList onSelect={setSelectedId} />
        </>
      ) : (
        <ReportDetail reportId={selectedId} />
      )}
    </div>
  );
}
